'use client';

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import * as Slider from '@radix-ui/react-slider';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import type { Category } from '@/types/tournament.model';
import type { Sport } from '@/types';
import { getEloCategories } from '@/api/elo/eloApi';
import type { EloCategory } from '@/components/tournament/models/elo.model';
import { handleGlobalError } from '@/utils/error-handler';
import {
  getCategoryAbbreviation,
  getCategorySum,
  getSumOptions,
  getIndexPairForSum,
} from '@/utils/category-utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface CategoryRangeSliderProps {
  onChange: (
    minCategory: Category,
    maxCategory: Category,
    forceSum: boolean,
  ) => void;
  sport: Sport;
  initialMinCategory?: Category;
  initialMaxCategory?: Category;
  initialForceSum?: boolean;
  /** When false, outer wrapper is a div (use inside parent Card). Default true. */
  wrapInCard?: boolean;
  /** Hide visible "Selecciona las categorías" when parent already shows section title. */
  hideInnerTitle?: boolean;
}

function SliderSurface({
  wrapInCard,
  className,
  children,
}: {
  wrapInCard: boolean;
  className?: string;
  children: ReactNode;
}) {
  if (wrapInCard) {
    return (
      <Card className={cn('space-y-6 p-4 sm:p-6', className)}>{children}</Card>
    );
  }
  return <div className={cn('space-y-6', className)}>{children}</div>;
}

export function CategoryRangeSlider({
  onChange,
  sport,
  initialMinCategory,
  initialMaxCategory,
  initialForceSum = false,
  wrapInCard = true,
  hideInnerTitle = false,
}: CategoryRangeSliderProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [range, setRange] = useState<[number, number]>([0, 0]);
  const [isRangeEnabled, setIsRangeEnabled] = useState(false);
  const [forceSum, setForceSum] = useState(initialForceSum);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  /** Avoid effect loops: parent often passes a new `onChange` every render (e.g. wizard `updateData`). */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Convert EloCategory to Category format
  const convertEloCategoriesToCategories = useCallback(
    (eloCategories: EloCategory[]): Category[] => {
      return eloCategories.map((eloCategory) => ({
        id: eloCategory.id,
        minimumElo: eloCategory.minimumElo,
        name: eloCategory.name,
        sport: eloCategory.sport,
      }));
    },
    [],
  );

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    if (!sport?.uid) {
      setCategoriesError('Sport UID is required');
      return;
    }

    setIsLoadingCategories(true);
    setCategoriesError(null);

    try {
      const eloCategories = await getEloCategories(sport.uid);
      const categoryList = convertEloCategoriesToCategories(eloCategories);
      setCategories(categoryList);
      return categoryList;
    } catch (error) {
      console.error('Error fetching Elo categories:', error);

      // Use global error handler to show toast with backend error message if available
      handleGlobalError(error, 'Error al cargar las categorías');

      // Set local error state for component UI
      setCategoriesError('Error al cargar las categorías');
      setCategories([]);
      return [];
    } finally {
      setIsLoadingCategories(false);
    }
  }, [sport, convertEloCategoriesToCategories]);

  // Initialize categories when sport changes
  useEffect(() => {
    if (sport?.uid) {
      setIsInitialized(false);
      fetchCategories();
    }
  }, [sport?.uid, fetchCategories]);

  // Initialize range and settings when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !isInitialized && !isLoadingCategories) {
      let minIndex = 0;
      let maxIndex = 0;
      let rangeEnabled = false;

      // Set initial values based on props
      if (initialMinCategory) {
        const foundMinIndex = categories.findIndex(
          (c) => c.id === initialMinCategory.id,
        );
        if (foundMinIndex !== -1) {
          minIndex = foundMinIndex;
        }
      }

      if (initialMaxCategory) {
        const foundMaxIndex = categories.findIndex(
          (c) => c.id === initialMaxCategory.id,
        );
        if (foundMaxIndex !== -1) {
          maxIndex = foundMaxIndex;
          rangeEnabled = foundMaxIndex !== minIndex;
        }
      }

      // Set all state at once to prevent flickering
      setRange([minIndex, maxIndex]);
      setIsRangeEnabled(rangeEnabled);
      setForceSum(initialForceSum);
      setIsInitialized(true);

      // Call onChange with initial values
      onChangeRef.current(
        categories[minIndex],
        categories[maxIndex],
        initialForceSum,
      );
    }
  }, [
    categories,
    initialMinCategory,
    initialMaxCategory,
    initialForceSum,
    isInitialized,
    isLoadingCategories,
  ]);

  // Handle changes after initialization
  useEffect(() => {
    if (categories.length > 0 && isInitialized) {
      onChangeRef.current(categories[range[0]], categories[range[1]], forceSum);
    }
  }, [range, forceSum, categories, isInitialized]);

  const handleRangeChange = (newRange: number[]) => {
    if (isRangeEnabled) {
      setRange([newRange[0], newRange[1]]);
    } else {
      setRange([newRange[0], newRange[0]]);
    }
  };

  const handleRangeCheckboxChange = (checked: boolean) => {
    setIsRangeEnabled(checked);
    if (checked && range[0] === range[1]) {
      const newMaxIndex =
        range[0] < categories.length - 1 ? range[0] + 1 : range[0];
      setRange([range[0], newMaxIndex]);
    } else if (!checked) {
      setRange([range[0], range[0]]);
      setForceSum(false);
    }
  };

  const sumOptions = getSumOptions(categories);
  const currentSum = getCategorySum(
    categories[range[0]],
    categories[range[1]],
    categories,
  );

  const handleSumChange = (sum: number) => {
    const pair = getIndexPairForSum(sum, categories.length);
    if (!pair) return;

    setRange(pair);
    setIsRangeEnabled(pair[0] !== pair[1]);
  };

  /**
   * Turning the sum on normalizes to the widest pair right away, so what gets
   * stored matches what the dropdown says.
   */
  const handleForceSumChange = (checked: boolean) => {
    setForceSum(checked);

    if (checked) {
      const target =
        currentSum != null && sumOptions.includes(currentSum)
          ? currentSum
          : sumOptions[Math.floor(sumOptions.length / 2)];
      handleSumChange(target);
    } else {
      setIsRangeEnabled(range[0] !== range[1]);
    }
  };

  const getConfigurationDescription = () => {
    if (categories.length === 0) return '';

    const minName = categories[range[0]]?.name ?? '';
    const maxName = categories[range[1]]?.name ?? '';

    if (forceSum) {
      return `Cualquier pareja cuyas categorías sumen ${currentSum ?? '—'}. Pueden anotarse de ${minName} a ${maxName}.`;
    }
    if (!isRangeEnabled) {
      return `Torneo de ${minName}.`;
    }
    return `Torneo de ${minName} a ${maxName}, sin restricción de suma.`;
  };

  // Show error state if categories failed to load (priority over loading)
  if (categoriesError && categories.length === 0 && !isLoadingCategories) {
    return (
      <SliderSurface wrapInCard={wrapInCard}>
        <Label
          className={
            hideInnerTitle
              ? 'sr-only'
              : 'text-lg font-semibold text-gray-800'
          }
        >
          Selecciona las categorías
        </Label>
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-600">{categoriesError}</p>
          <button
            onClick={() => fetchCategories()}
            className="mt-2 text-sm text-red-700 underline hover:text-red-800"
          >
            Reintentar
          </button>
        </div>
      </SliderSurface>
    );
  }

  // Show loading state while fetching categories
  if (
    isLoadingCategories ||
    (!isInitialized && categories.length === 0 && !categoriesError)
  ) {
    return (
      <SliderSurface wrapInCard={wrapInCard}>
        <Label
          className={
            hideInnerTitle
              ? 'sr-only'
              : 'text-lg font-semibold text-gray-800'
          }
        >
          Selecciona las categorías
        </Label>
        <div className="animate-pulse">
          <div className="mb-4 h-5 rounded bg-gray-200"></div>
          <div className="space-y-2">
            <div className="h-4 w-1/2 rounded bg-gray-200"></div>
            <div className="h-4 w-1/3 rounded bg-gray-200"></div>
          </div>
        </div>
        {isLoadingCategories && (
          <p className="text-sm text-gray-500">Cargando categorías...</p>
        )}
      </SliderSurface>
    );
  }

  return (
    <SliderSurface wrapInCard={wrapInCard}>
      <Label
        htmlFor="category-range"
        className={
          hideInnerTitle
            ? 'sr-only'
            : 'text-lg font-semibold text-gray-800'
        }
      >
        Selecciona las categorías
      </Label>
      {!forceSum && (
        <div className="relative pb-4 pt-12">
          {/* Category labels positioned above the slider */}
          <div className="absolute left-0 right-0 top-0 flex h-8 items-center">
            {categories.map((category, index) => {
              // Radix UI sliders typically have 10px padding on each side for the thumb
              const thumbPadding = 10;
              const availableWidth = `calc(100% - ${thumbPadding * 2}px)`;
              const percentage =
                categories.length === 1
                  ? 50
                  : (index / (categories.length - 1)) * 100;

              return (
                <div
                  key={category.id}
                  className={`absolute flex items-center justify-center rounded-full font-medium
                    h-7 w-7 text-[10px] sm:h-8 sm:w-8 sm:text-xs ${
                    (isRangeEnabled && index >= range[0] && index <= range[1]) ||
                    (!isRangeEnabled && index === range[0])
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={{
                    left: `calc(${thumbPadding}px + ${availableWidth} * ${percentage / 100} - 0.875rem)`,
                  }}
                  title={category.name}
                >
                  {getCategoryAbbreviation(index, categories)}
                </div>
              );
            })}
          </div>

          {/* Slider component */}
          <Slider.Root
            className="relative flex h-5 w-full touch-none select-none items-center"
            value={isRangeEnabled ? range : [range[0]]}
            onValueChange={handleRangeChange}
            min={0}
            max={categories.length - 1}
            step={1}
            minStepsBetweenThumbs={1}
            aria-label="Rango de categorías"
          >
            <Slider.Track className="relative h-2 grow rounded-full bg-gray-200">
              {isRangeEnabled && (
                <Slider.Range className="absolute h-full rounded-full bg-blue-600" />
              )}
            </Slider.Track>
            {(isRangeEnabled ? range : [range[0]]).map((_value, index) => (
              <Slider.Thumb
                key={index}
                className="block h-5 w-5 rounded-full bg-white shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ))}
          </Slider.Root>
        </div>
      )}
      <div className="space-y-3">
        {!forceSum && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="range-enabled"
              checked={isRangeEnabled}
              onCheckedChange={handleRangeCheckboxChange}
            />
            <Label
              htmlFor="range-enabled"
              className="text-sm font-medium text-gray-700"
            >
              Habilitar selección de rango
            </Label>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Checkbox
            id="force-sum"
            checked={forceSum}
            onCheckedChange={(checked) => handleForceSumChange(checked === true)}
          />
          <Label
            htmlFor="force-sum"
            className="text-sm font-medium text-gray-700"
          >
            Aplicar restricción de suma
          </Label>
        </div>

        {forceSum && (
          <div className="flex items-center gap-3 pt-1">
            <Label
              htmlFor="sum-value"
              className="text-sm font-medium text-gray-700"
            >
              Suma:
            </Label>
            <Select
              value={currentSum != null ? String(currentSum) : undefined}
              onValueChange={(value) => handleSumChange(Number(value))}
            >
              <SelectTrigger id="sum-value" className="w-[110px]">
                <SelectValue placeholder="Elegí" />
              </SelectTrigger>
              <SelectContent>
                {sumOptions.map((sum) => (
                  <SelectItem key={sum} value={String(sum)}>
                    {sum}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="h-12 text-sm font-medium text-gray-600">
        {getConfigurationDescription()}
      </div>
    </SliderSurface>
  );
}
